import {
  ColDef,
  GridApi,
  ICellRendererParams,
  IRowNode,
} from 'ag-grid-community'
import _ from 'lodash'
import {
  FunctionProperty,
  PropertyType,
} from '../../../../lib/commons/appFunction'
import validator from '../../meta/validator'
import SelectVO from '../../../../vo/SelectVO'

export class InputError {
  protected errors: {
    [id: string]: {
      [field: string]: string
    }
  } = {}

  public add({
    id,
    field,
    message,
  }: {
    id: string
    field: string
    message: string
  }): void {
    if (!this.errors[id]) {
      this.errors[id] = {}
    }
    this.errors[id][field] = message
  }

  public remove({ id, field }: { id: string; field: string }) {
    if (!this.errors[id]) {
      return
    }
    delete this.errors[id][field]
    if (_.isEmpty(this.errors[id])) {
      delete this.errors[id]
    }
  }

  public removeErrorsById(id: string) {
    if (!this.errors[id]) {
      return
    }
    delete this.errors[id]
  }

  public hasMessage(): boolean {
    return 0 < Object.keys(this.errors).length
  }

  public toMessage(idConverter: (id) => string = _ => _) {
    const messages: string[] = []
    const map = {}
    Object.keys(this.errors).forEach(id => {
      map[idConverter(id)] = id
    })
    Object.keys(map)
      .sort((a, b) => (a === b ? 0 : Number(a) > Number(b) ? 1 : -1))
      .forEach(alt => {
        const id = map[alt]
        const row = this.errors[id]
        const fields = Object.keys(row)
        fields.forEach(field => {
          messages.push(`#${alt} > ${row[field]}`)
        })
      })
    return messages.join('\n')
  }
}

export const setError = (
  data,
  message,
  { context, node, colDef }: ICellRendererParams
) => {
  const errors: InputError = context.errors
  if (errors && data.edited) {
    if (message) {
      addInputError(errors, node.id, message, colDef)
    } else {
      errors.remove({ id: node.id ?? '', field: colDef?.field ?? '' })
    }
  }
  return message
}

export const addInputError = (
  errors: InputError,
  nodeId: string | undefined,
  message: string,
  colDef: ColDef | undefined
) => {
  errors.add({
    id: nodeId ?? '',
    field: colDef?.field ?? '',
    message: `${colDef?.headerName} > ${message}`,
  })
}

const getValidateValue = (
  gridApi: GridApi,
  node: IRowNode,
  colId: string,
  uiMeta: FunctionProperty
) => {
  if (!gridApi) return
  const val = gridApi.getCellValue({ colKey: colId, rowNode: node })
  if (uiMeta?.propertyType === PropertyType.Select) {
    const target = uiMeta.valuesAllowed.find(v => v.value === val)
    return target ? SelectVO.fromCustomEnum(target) : val
  }
  return val
}

export const validateNodes = ({
  gridApi,
  uuids,
  extraValidate,
}: {
  gridApi: GridApi | undefined | null
  uuids: string[]
  extraValidate?: (
    colId: string | undefined,
    node: IRowNode | undefined,
    colDef: ColDef
  ) => string | undefined
}): InputError => {
  const errors = new InputError()
  if (!gridApi) return errors

  const colIds = gridApi.getColumnState().map(colState => colState.colId)

  uuids.forEach(uuid => {
    const errInfos = validateNode({
      gridApi,
      uuid,
      columnIds: colIds,
      extraValidate,
    })
    if (errInfos) {
      errInfos.forEach(info =>
        addInputError(errors, info.nodeId, info.message, info.colDef)
      )
    }
  })

  return errors
}

export const validateNode = ({
  gridApi,
  uuid,
  columnIds,
  extraValidate,
}: {
  gridApi: GridApi | undefined | null
  uuid: string
  columnIds: string[]
  extraValidate?: (
    colId: string | undefined,
    node: IRowNode | undefined,
    colDef: ColDef
  ) => string | undefined
}):
  | {
      nodeId: string | undefined
      message: string
      colDef: ColDef
    }[]
  | undefined => {
  if (!gridApi) return
  const node: IRowNode | undefined = gridApi.getRowNode(uuid)
  if (node === undefined) return

  const errors: {
    nodeId: string | undefined
    message: string
    colDef: ColDef
  }[] = []

  columnIds?.forEach(colId => {
    const colDef = gridApi.getColumn(colId)?.getColDef()
    if (!colDef || !colDef.cellRendererParams) return
    // TODO Reconsider implementation.
    if (colDef.cellRendererParams.isAdditionalProperty) {
      // ref: src/view/containers/BulkSheetView/hooks/wbsItemAdditionalProperties/columnDef/index.ts
      const val = gridApi.getCellValue({ colKey: colId, rowNode: node })
      const err = colDef.cellRendererParams.validate(val, { data: node.data })
      if (err) {
        errors.push({
          nodeId: node.id,
          message: err,
          colDef,
        })
      }
      return
    }
    const uiMeta: FunctionProperty = colDef.cellRendererParams.uiMeta
    const val = getValidateValue(gridApi, node, colId, uiMeta)
    if (extraValidate) {
      const err = extraValidate(colId, node, colDef)
      if (err) {
        errors.push({
          nodeId: node.id,
          message: err,
          colDef,
        })
      }
    }
    const err = uiMeta
      ? validator
          .validate(val, node.data, uiMeta, () => undefined)
          ?.getMessage()
      : undefined
    if (err) {
      errors.push({
        nodeId: node.id,
        message: err,
        colDef,
      })
    }
  })
  return errors
}
